import { useCallback, useEffect, useState } from 'react';

import type { AgentApiCandidateProfilesItem } from '@/types/serializers';
import useFetchCandidateMailSetting from '../api/useFetchCandidateMailSetting';
import useSearchCandidateProfiles from '../api/useSearchCandidateProfiles';
import { hasEmail, toRecipient, type CandidateRecipient } from '../lib/recipients';

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

export default function useCandidateRecipientSelection(
  initialRecipients: CandidateRecipient[] = []
) {
  const [recipients, setRecipients] = useState<CandidateRecipient[]>(initialRecipients);
  const [hasNotifiedRecipientLimit, setHasNotifiedRecipientLimit] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedOrganizationUserId, setSelectedOrganizationUserId] = useState<number | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const { data: mailSettingData } = useFetchCandidateMailSetting();
  const mailRecipientLimit = mailSettingData?.mail_recipient_limit ?? null;
  const debouncedKeyword = useDebouncedValue(searchKeyword, 300);
  const {
    data: searchResult,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useSearchCandidateProfiles({
    keyword: debouncedKeyword,
    organizationUserId: selectedOrganizationUserId ?? undefined,
    customTagIds: selectedTagIds,
    enabled: isPickerOpen,
    perPage: 30,
  });
  const candidates = (searchResult?.candidates ?? []).filter(hasEmail);
  const totalCount = searchResult?.totalCount ?? 0;
  const isLimitReached =
    mailRecipientLimit !== null &&
    mailRecipientLimit > 0 &&
    recipients.length >= mailRecipientLimit;
  const limitStatusText =
    mailRecipientLimit === 0
      ? '送信先を選択できません。'
      : isLimitReached && mailRecipientLimit !== null
        ? `送信先を最大${mailRecipientLimit}件まで選択できます。（上限に達しています）`
        : null;

  useEffect(() => {
    if (mailRecipientLimit === null || mailRecipientLimit <= 0) {
      if (hasNotifiedRecipientLimit) {
        setHasNotifiedRecipientLimit(false);
      }
      return;
    }

    if (recipients.length === mailRecipientLimit) {
      if (!hasNotifiedRecipientLimit) {
        window.alert(`送信先を最大${mailRecipientLimit}件まで選択できます。`);
        setHasNotifiedRecipientLimit(true);
      }
    } else if (recipients.length < mailRecipientLimit && hasNotifiedRecipientLimit) {
      setHasNotifiedRecipientLimit(false);
    }
  }, [hasNotifiedRecipientLimit, mailRecipientLimit, recipients.length]);

  useEffect(() => {
    if (!isPickerOpen) {
      setSearchKeyword('');
      setSelectedOrganizationUserId(null);
      setSelectedTagIds([]);
    }
  }, [isPickerOpen]);

  const toggleRecipient = useCallback(
    (candidate: AgentApiCandidateProfilesItem) => {
      setRecipients((prev) => {
        const exists = prev.some((recipient) => recipient.id === candidate.id);
        if (exists) {
          return prev.filter((recipient) => recipient.id !== candidate.id);
        }
        if (mailRecipientLimit !== null && prev.length >= mailRecipientLimit) {
          if (mailRecipientLimit === 0) {
            window.alert('送信先を選択できません。');
          } else {
            window.alert(`送信先は最大${mailRecipientLimit}件まで選択できます。`);
          }
          return prev;
        }
        return [...prev, toRecipient(candidate)];
      });
    },
    [mailRecipientLimit]
  );

  const removeRecipient = useCallback((candidateId: number) => {
    setRecipients((prev) => prev.filter((recipient) => recipient.id !== candidateId));
  }, []);

  const clearRecipients = useCallback(() => {
    setRecipients([]);
  }, []);

  const selectAllVisible = useCallback(() => {
    setRecipients((prev) => {
      const existingIds = new Set(prev.map((recipient) => recipient.id));
      const next = [...prev];
      let reachedLimit = false;
      for (const candidate of candidates) {
        if (existingIds.has(candidate.id)) continue;
        if (mailRecipientLimit !== null && next.length >= mailRecipientLimit) {
          reachedLimit = true;
          break;
        }
        next.push(toRecipient(candidate));
      }
      if (reachedLimit && mailRecipientLimit !== null) {
        window.alert(`送信先は最大${mailRecipientLimit}件まで選択できます。`);
      }
      return next;
    });
  }, [candidates, mailRecipientLimit]);

  const loadMore = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    void fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    recipients,
    toggleRecipient,
    removeRecipient,
    clearRecipients,
    selectAllVisible,
    mailRecipientLimit,
    isLimitReached,
    limitStatusText,
    isPickerOpen,
    openPicker: () => setIsPickerOpen(true),
    closePicker: () => setIsPickerOpen(false),
    searchKeyword,
    setSearchKeyword,
    selectedOrganizationUserId,
    setSelectedOrganizationUserId,
    selectedTagIds,
    setSelectedTagIds,
    candidates,
    totalCount,
    isLoading,
    isRefreshing: isFetching && !isLoading && !isFetchingNextPage,
    isFetchingNextPage,
    hasNextPage: Boolean(hasNextPage),
    loadMore,
    errorMessage:
      isError && searchResult === undefined
        ? '求職者の取得に失敗しました。時間を置いて再度お試しください。'
        : null,
    isEmpty: !isLoading && !isFetchingNextPage && candidates.length === 0 && totalCount === 0,
  };
}

export type CandidateRecipientSelection = ReturnType<typeof useCandidateRecipientSelection>;
